import firebase from '@/firebase'

/**
 * Firestore Debug Logger
 *
 * Wraps Firestore db object to log all operations to console when ?debug=true is in URL
 * Provides clean, hierarchical logging of all Firestore operations
 */

// Check if debug mode is enabled via URL parameter
const isDebugEnabled = () => {
  if (typeof window === 'undefined') return false
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('debug') === 'true'
}

// Detect dark mode
const isDarkMode = () => {
  if (typeof window === 'undefined') return false
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
}

// Color schemes for different operation types (light mode)
const lightModeColors = {
  collection: '#FF6B35', // Orange
  doc: '#4ECDC4', // Teal
  get: '#2ECC71', // Green
  set: '#E74C3C', // Red
  update: '#9B59B6', // Purple
  delete: '#C0392B', // Dark red
  onSnapshot: '#16A085', // Dark teal
  where: '#F39C12', // Dark yellow
  orderBy: '#8E44AD', // Dark violet
  add: '#00ADB5', // Dark cyan
  batch: '#E056FD' // Magenta
}

// Color schemes for dark mode (brighter, more saturated)
const darkModeColors = {
  collection: '#FF8C5A', // Bright orange
  doc: '#5EDDD4', // Bright teal
  get: '#52DE97', // Bright green
  set: '#FF6B6B', // Bright red
  update: '#C77DFF', // Bright purple
  delete: '#FF5252', // Bright red
  onSnapshot: '#4ECDC4', // Bright teal
  where: '#FFD93D', // Bright yellow
  orderBy: '#A78BFA', // Bright violet
  add: '#38EDF0', // Bright cyan
  batch: '#FF85E0' // Bright magenta
}

// Get the appropriate color based on mode
const getColor = (operation) => {
  const colors = isDarkMode() ? darkModeColors : lightModeColors
  return colors[operation] || (isDarkMode() ? '#AAA' : '#666')
}

// Statistics tracking
const stats = {
  totalCalls: 0,
  callsByType: {},
  startTime: Date.now()
}

/**
 * Creates a formatted log message with operation details
 */
const createLogMessage = (operation, path, details = {}) => {
  stats.totalCalls++
  stats.callsByType[operation] = (stats.callsByType[operation] || 0) + 1

  const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false })
  const color = getColor(operation)
  const dark = isDarkMode()

  let message = `%c🔥 [${operation.toUpperCase()}] %c${path}`
  const styles = [
    `color: ${color}; font-weight: bold;`,
    `color: ${dark ? '#E0E0E0' : '#333'}; font-weight: normal;`
  ]

  // Add details if present
  if (details.docCount !== undefined) {
    message += ` %c→ ${details.docCount} docs`
    styles.push(`color: ${dark ? '#B0B0B0' : '#666'}; font-style: italic;`)
  }

  if (details.duration !== undefined) {
    message += ` %c⏱️ ${details.duration}ms`
    styles.push(`color: ${dark ? '#999' : '#888'}; font-size: 0.9em;`)
  }

  if (details.data) {
    message += ` %c📦 ${JSON.stringify(details.data).substring(0, 100)}${JSON.stringify(details.data).length > 100 ? '...' : ''}`
    styles.push(`color: ${dark ? '#A0A0A0' : '#777'}; font-size: 0.85em;`)
  }

  message += ` %c[${timestamp}]`
  styles.push(`color: ${dark ? '#808080' : '#AAA'}; font-size: 0.8em;`)

  return { message, styles }
}

/**
 * Wraps a Firestore reference to track and log operations
 */
class FirestoreDebugWrapper {
  constructor (ref, path = '', isQuery = false) {
    this.ref = ref
    this.path = path
    this.isQuery = isQuery

    // Return a Proxy to intercept all method calls
    return new Proxy(this, {
      get (target, prop) {
        // Return the wrapped method if it exists
        if (prop in target) {
          return target[prop]
        }

        // Pass through to the actual Firestore reference
        const original = target.ref[prop]

        if (typeof original === 'function') {
          return function (...args) {
            const result = original.apply(target.ref, args)

            // Handle chaining methods
            if (prop === 'collection' && args[0]) {
              const newPath = target.path ? `${target.path}/collection('${args[0]}')` : `collection('${args[0]}')`
              return new FirestoreDebugWrapper(result, newPath)
            }

            if (prop === 'doc') {
              const docId = args[0] || '[auto-id]'
              const newPath = `${target.path}/doc('${docId}')`
              return new FirestoreDebugWrapper(result, newPath)
            }

            if (prop === 'where' && args.length >= 2) {
              const newPath = `${target.path}.where('${args[0]}', '${args[1]}', ${JSON.stringify(args[2])})`
              return new FirestoreDebugWrapper(result, newPath, true)
            }

            if (prop === 'orderBy' && args[0]) {
              const direction = args[1] || 'asc'
              const newPath = `${target.path}.orderBy('${args[0]}', '${direction}')`
              return new FirestoreDebugWrapper(result, newPath, true)
            }

            // Handle terminal operations
            if (prop === 'get') {
              const startTime = performance.now()
              return result.then(snapshot => {
                const duration = Math.round(performance.now() - startTime)
                const docCount = snapshot.docs ? snapshot.docs.length : (snapshot.exists ? 1 : 0)
                const log = createLogMessage('get', target.path, { docCount, duration })
                console.log(log.message, ...log.styles)
                return snapshot
              })
            }

            if (prop === 'set' && args[0]) {
              const log = createLogMessage('set', target.path, { data: args[0] })
              console.log(log.message, ...log.styles)
            }

            if (prop === 'update' && args[0]) {
              const log = createLogMessage('update', target.path, { data: args[0] })
              console.log(log.message, ...log.styles)
            }

            if (prop === 'delete') {
              const log = createLogMessage('delete', target.path)
              console.log(log.message, ...log.styles)
            }

            if (prop === 'add' && args[0]) {
              const log = createLogMessage('add', target.path, { data: args[0] })
              console.log(log.message, ...log.styles)
            }

            if (prop === 'onSnapshot') {
              const callback = args[0]
              const errorCallback = args[1]

              // Wrap the callback to log when snapshot is received
              const wrappedCallback = (snapshot) => {
                const docCount = snapshot.docs ? snapshot.docs.length : (snapshot.exists ? 1 : 0)
                const log = createLogMessage('onSnapshot', target.path, { docCount })
                console.log(log.message, ...log.styles)

                if (typeof callback === 'function') {
                  return callback(snapshot)
                }
              }

              // Call original with wrapped callback
              if (errorCallback) {
                return original.apply(target.ref, [wrappedCallback, errorCallback])
              } else {
                return original.apply(target.ref, [wrappedCallback])
              }
            }

            return result
          }
        }

        return original
      }
    })
  }
}

/**
 * Creates a debug-enabled db object
 */
const createDebugDb = () => {
  if (!isDebugEnabled()) {
    return firebase.db
  }

  const dark = isDarkMode()
  console.log(
    '%c🔥 FIRESTORE DEBUG MODE ENABLED 🔥',
    `color: ${dark ? '#FF8C5A' : '#FF6B35'}; font-size: 16px; font-weight: bold; background: ${dark ? '#2C2C2C' : '#FFF3E0'}; padding: 10px; border-radius: 5px;`
  )
  console.log(
    '%cAll Firestore operations will be logged to console',
    `color: ${dark ? '#AAA' : '#666'}; font-style: italic;`
  )

  // Create a proxy for the db object
  const dbProxy = new Proxy(firebase.db, {
    get (target, prop) {
      const original = target[prop]

      if (prop === 'collection' && typeof original === 'function') {
        return function (collectionPath) {
          const result = original.apply(target, [collectionPath])
          return new FirestoreDebugWrapper(result, `collection('${collectionPath}')`)
        }
      }

      if (prop === 'batch' && typeof original === 'function') {
        return function () {
          const log = createLogMessage('batch', 'db.batch()')
          console.log(log.message, ...log.styles)
          return original.apply(target, arguments)
        }
      }

      return original
    }
  })

  // Log statistics on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      const duration = Math.round((Date.now() - stats.startTime) / 1000)
      const dark = isDarkMode()
      console.group(
        '%c📊 Firestore Debug Statistics',
        `color: ${dark ? '#A78BFA' : '#6C5CE7'}; font-size: 14px; font-weight: bold;`
      )
      console.log(`Total operations: ${stats.totalCalls}`)
      console.log(`Session duration: ${duration}s`)
      console.log('Operations by type:', stats.callsByType)
      console.groupEnd()
    })
  }

  return dbProxy
}

export default {
  db: createDebugDb(),
  isDebugEnabled
}
